// R8 — **THE TWO SURFACES THAT TELL A MEMBER HER READING IS STILL COMING, AND THE PARTITION THEY
// ARE HALVES OF.**
//
// `GenerationQueueStrip` is the IN-ROOM strip: the work belonging to the room she is standing in.
// `GenerationFollower` is the SITE-WIDE dock: the work that belongs to every OTHER room, which is
// the whole point of it — N1-2 exists because a member who pressed Generate on Human Design and
// walked away had nothing anywhere telling her it was still going.
//
// ================================================================================================
// **THEY ARE A PARTITION, AND UNTIL THIS EXTRACTION NOTHING RAN IT.**
//
// One filters `g.room !== currentRoom`; the other filters `g.room === room`. Together every running
// generation was meant to reach EXACTLY ONE of them — **and R218 measured that seven of nine rooms
// draw no strip at all, so for those the dock's silence handed the row to nobody.** `dockCarries`
// below is the repaired half; the two failures this paragraph already named are still real:
//
//   · Both written `===` and a member who leaves the room is told **nothing, forever**, which is
//     N1-2's own reported defect put straight back.
//   · Both written `!==` and the room she is standing in stops reporting its own work while the
//     dock reports it twice.
//
// Neither shows an error, neither fails a build, and inside App.tsx the two predicates sat 30 lines
// apart with nothing able to ask whether they still agreed. `generationBelongsToRoom` is the ONE
// declaration both now read, and `scripts/funnel-capture.mjs` DRIVES the partition over a queue this
// house does not contain — the currency this ceiling is usually paid in (R8's own line: the thing
// that had to move is the thing nothing could reach).
// ================================================================================================
import { useEffect, useState, useLayoutEffect } from "react";
import type { CSSProperties } from "react";
import { X } from "lucide-react";
import { t } from "../strings.js";
import { modalRunState, photoRunState } from "../generations.js";
import { elapsedLabel } from "../lib/labels";
import { openShelves } from "../lib/shelves-door";
import { roomDrawsOwnQueue } from "../rooms.js";
import { GenerationLine } from "./GenerationLine";

/**
 * **DOES THIS GENERATION BELONG TO THE ROOM THE MEMBER IS STANDING IN?**
 *
 * The in-room strip draws it when this is TRUE; the site-wide dock draws it when this is FALSE. One
 * declaration, so the two can never drift into agreeing (which hides work) or into disagreeing twice
 * (which shows it twice).
 *
 * **A GENERATION WITH NO ROOM BELONGS TO NO ROOM, SO THE DOCK CARRIES IT** — never the strip. R17's
 * own finding is that five generator kinds resolved to NO ROOM, and the safe direction for a member
 * is the surface that follows her everywhere rather than one she may never stand in (R19: the
 * expensive failure is the silent one, and an unroomed reading nobody ever mentions is that failure).
 */
export const generationBelongsToRoom = (
  generation: { room?: string | null },
  room: string | null | undefined,
): boolean => Boolean(generation?.room) && Boolean(room) && generation.room === room;

/**
 * **DOES THE SITE-WIDE DOCK CARRY THIS GENERATION?** — R218.
 *
 * The dock used to ask `!generationBelongsToRoom(g, currentRoom)` and nothing else, on N1-2's stated
 * premise that a room she is standing in draws its own strip. **Seven of nine rooms draw none**
 * (`QUEUE_STRIP_ROOMS` in `src/rooms.js` carries the whole finding), so for 28 of the 33 kinds this
 * site can register the row reached NEITHER surface — a partition that dropped rows on the floor.
 *
 * Two facts, asked separately (R18): *does this belong to the room she is standing in*, and *does
 * that room say so itself*. Only when BOTH hold does the dock stay quiet. Written as one exported
 * declaration rather than composed at the call site, because a composition typed into a component is
 * one no gate can RUN (R8/R95) — and this one is the difference between a member seeing her paid
 * reading and seeing nothing.
 */
export const dockCarries = (
  generation: { room?: string | null },
  currentRoom: string | null | undefined,
): boolean => !(generationBelongsToRoom(generation, currentRoom) && roomDrawsOwnQueue(currentRoom));

// G1 PART B: what is running for this member, RIGHT NOW, read from the server.
//
// The truth is server-side on purpose (slice 1): React state dies on navigation and on refresh,
// which is exactly the owner's complaint — she pressed Generate, the button said "generating"
// once, and then nothing could tell her it was still working. Because this asks the server, the
// answer survives changing pages, refreshing, and closing the tab and coming back.
//
// It must never make noise: the endpoint never 401s and never throws, and a failed poll here is
// silently ignored rather than shown to a member who did nothing wrong.
export type ActiveGeneration = { id: string; label: string; startedAt: number; room?: string | null; kind?: string | null };
export type RecentGeneration = { id: string; label: string; room?: string | null; savedId?: number | null; finishedAt: number };

export function useActiveGenerations(enabled: boolean) {
  const [active, setActive] = useState<ActiveGeneration[]>([]);
  const [recent, setRecent] = useState<RecentGeneration[]>([]);

  useEffect(() => {
    if (!enabled) { setActive([]); setRecent([]); return; }
    let alive = true;
    let timer = 0;
    const poll = async () => {
      try {
        const r = await fetch("/api/generations/active");
        const d = await r.json();
        if (alive && d?.ok) {
          if (Array.isArray(d.active)) setActive(d.active);
          if (Array.isArray(d.recent)) setRecent(d.recent);
        }
      } catch {
        // A poll that cannot reach the server proves nothing about the member's work; say nothing.
      }
      if (alive) timer = window.setTimeout(poll, 3000);
    };
    poll();
    return () => { alive = false; window.clearTimeout(timer); };
  }, [enabled]);

  return { active, recent };
}

// X3: the floating "Being prepared" side tray was removed (it overlapped the menu; the signal lives ON
// each control now). `useActiveGenerations` stays — MyRoom reads it to keep the report chips honest
// about what is in flight. N1-2 below carries the rest of that reasoning; it is not restated here.

// N1-2 — THE GENERATION FOLLOWS YOU, SITE-WIDE.
//
// The work already ran detached server-side, and `/api/generations/active` already knew about it. What
// was missing is that only TWO rooms rendered the in-room strip, and the strip only ever shows its OWN
// room's work — so a member who pressed Generate on Human Design and walked away had nothing anywhere
// telling her it was still going. That is exactly what the owner saw.
//
// This is mounted ONCE, at app level, so it is on every page of the house. It reads the SAME
// `useActiveGenerations` the in-room strip reads — one mechanism, one server truth, no second poller.
// R218: it hides work belonging to the room the member is standing in **ONLY WHEN THAT ROOM REALLY
// DRAWS ITS OWN STRIP** — the sentence that stood here said it always did, and this decision's own
// diagnosis four lines above names the fact that refutes it. Two things saying one thing on one
// screen is noise; nothing saying it is the defect N1-2 exists to end.
//
// It docks bottom-LEFT, deliberately: the drawer rail owns the right edge and the mobile tab bar owns
// the bottom. The floating tray that was removed in X3 sat over the menu; this one cannot.
// R17: ROOM_FOR_PAGE moved to src/rooms.js, beside SHELF_ROOMS, so a gate can import both.
// 14-B8: `language` is REQUIRED — this rides on EVERY page, so an optional prop nobody passes leaves
// the whole house English while every room in it turns (R27).
// R49 — ONE LAW FOR BOTH GENERATION SURFACES, WHICH PAID FOR THIS ROUND'S LINES (`useEscapeKey`'s
// currency): the same poll, dismissed set, two filters and per-second interval, byte for byte. The
// dashboard's three `now` stamps are NOT folded in — freshness, not a clock. Details in DECISIONS.
function useGenerationQueue(mine: (g: { room?: string | null }) => boolean) {
  const { active, recent } = useActiveGenerations(true);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [now, setNow] = useState(() => Date.now());
  const running = active.filter(mine);
  const ready = recent.filter((g) => mine(g) && !dismissed.has(g.id));
  // Ticks only while something is running: a timer with nothing to time re-renders forever.
  useEffect(() => {
    if (!running.length) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [running.length]);
  return { running, ready, now, dismiss: (id: string) => setDismissed((s) => new Set(s).add(id)) };
}

export function GenerationFollower({ currentRoom, language }: { currentRoom: string | null; language: string }) {
  // R218: the dock carries everything the room she is standing in does not ALREADY SAY FOR ITSELF —
  // the other half of the one partition above, so this can never quietly start agreeing with the
  // strip, and can no longer stay quiet about a room that has no strip to be quiet on behalf of.
  const { running, ready, now, dismiss } = useGenerationQueue((g) => dockCarries(g, currentRoom));
  // ================================================================================
  // >>> **THE DOCK NEVER COVERS A LALA COMPOSER (her ruling, 2026-08-25).** <<<
  // ================================================================================
  // MEASURED at coordinates on her funnel's own landing: `.gen-follow` is `position: fixed;
  // z-index: 68` and on a phone shell it sits at `bottom: calc(var(--tabbar-h) + safe-area)` —
  // exactly where the Ask Lala composer is — while `.gen-follow__row` sets `pointer-events: auto`
  // so its own Open and Close controls work. A tap at the Send button's own centre answered
  // `SPAN.gen-follow__time`, then `BUTTON.gen-follow__close`. **Her ruling is SITE-WIDE**, so this
  // asks about a composer rather than about a page: the funnel arrival is simply the member most
  // likely to meet it, because F4-3 lands her in the conversation with two generations in flight.
  //
  // >>> IT IS THE COMPOSER'S OWN MEASURED CLEARANCE AND NEVER A CONSTANT, AND ITEM 2's OWN ROUND
  // IS WHY. <<< S6-2 chose 176px against a composer that then GREW (THE COMPOSER GROWS made it
  // grow), and the number rotted where nobody could see it — a hand-tuned px clearance is the one
  // repair this house has already proved wrong on this exact pair of surfaces. `NavHeightVar`'s
  // own pattern: measure the thing, publish the number, let one `calc()` do the arithmetic.
  //
  // **IT IS ZERO WHEN NO COMPOSER IS MOUNTED, SO EVERY OTHER PAGE IN THE HOUSE IS BYTE-IDENTICAL**
  // (ITEM 12's neutrality argument) — and the CSS falls back to `0px`, so the failure mode of this
  // whole mechanism is exactly today's behaviour and never something worse (RAIL RESTORE's ruling
  // on `var(--nav-real-h, var(--nav-h))`).
  const [lift, setLift] = useState(0);
  useLayoutEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return undefined;
    const measure = () => {
      // The LOWEST composer on screen decides, because the dock is bottom-anchored and what it has
      // to clear is how far up from the viewport foot that composer's top edge sits. Both of this
      // house's Lala doors render `.ask-lala-compose` (one mind, two doors), so neither is named.
      let clear = 0;
      for (const el of Array.from(document.querySelectorAll(".ask-lala-compose"))) {
        const r = el.getBoundingClientRect();
        if (r.height <= 0) continue;
        // A composer scrolled off the foot answers a NEGATIVE clearance and lifts nothing.
        clear = Math.max(clear, Math.round(window.innerHeight - r.top));
      }
      setLift(Number.isFinite(clear) && clear > 0 ? clear : 0);
    };
    measure();
    // W4-7's growing textarea changes height as she types, and a member typing a long question is
    // exactly when the dock would slide back over her Send control.
    let ro = null;
    const first = document.querySelector(".ask-lala-compose");
    if (first && typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(measure); ro.observe(first); }
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("resize", measure); if (ro) ro.disconnect(); };
    // `now` ticks once a second while anything is running, which is the window this is about.
  }, [running.length, ready.length, now]);
  if (!running.length && !ready.length) return null;
  const openIt = (g: RecentGeneration) => { openShelves({ room: g.room, itemId: g.savedId }); dismiss(g.id); };
  return (
    <div className="gen-follow" role="status" aria-live="polite"
      style={lift ? ({ "--gen-follow-lift": `${lift}px` } as CSSProperties) : undefined}>
      {running.map((g) => (
        <div key={g.id} className="gen-follow__row is-working">
          <span className="gen-follow__pulse" aria-hidden="true" />
          <span className="gen-follow__text">
            <GenerationLine language={language} textKey="gen.preparing" label={g.label} />
            <span className="gen-follow__time">{elapsedLabel(now - g.startedAt)}</span>
          </span>
        </div>
      ))}
      {ready.map((g) => (
        <div key={g.id} className="gen-follow__row is-ready">
          <span className="gen-follow__text"><GenerationLine language={language} textKey="gen.readyNamed" label={g.label} /></span>
          <button type="button" className="gen-follow__open" onClick={() => openIt(g)}>{t(language, "gen.open")}</button>
          <button type="button" className="gen-follow__close" aria-label={t(language, "gen.dismissNamed", { label: g.label })} onClick={() => dismiss(g.id)}>
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function GenerationQueueStrip({ room, onOpen, onCreateAnother, createLabel, language }: {
  room: string;
  onOpen?: (savedId: number | null, room: string | null | undefined) => void;
  onCreateAnother?: () => void;
  createLabel?: string;
  language: string;
}) {
  const { running: mineActive, ready: mineRecent, now, dismiss } = useGenerationQueue((g) => generationBelongsToRoom(g, room));
  if (!mineActive.length && !mineRecent.length) return null;
  return (
    <div className="gen-queue" role="status" aria-live="polite">
      {mineActive.length ? <p className="gen-queue__note">{t(language, mineActive.length > 1 ? "gen.queue.noteMany" : "gen.queue.noteOne")}</p> : null}
      <ul className="gen-queue__list">
        {mineActive.map((g) => (
          <li key={g.id} className="gen-queue__item gen-queue__item--running">
            <span className="gen-queue__pulse" aria-hidden="true" />
            <span className="gen-queue__label">{g.label}</span>
            <span className="gen-queue__meta">{t(language, "gen.stillWorking", { time: elapsedLabel(now - g.startedAt) })}</span>
          </li>
        ))}
        {mineRecent.map((g) => (
          <li key={g.id} className="gen-queue__item gen-queue__item--ready">
            <span className="gen-queue__check" aria-hidden="true">✦</span>
            <span className="gen-queue__label">{g.label}</span>
            <span className="gen-queue__meta gen-queue__meta--ready">{t(language, "gen.readyShort")}</span>
            {onOpen && (
              <button type="button" className="gen-queue__open" onClick={() => onOpen(g.savedId ?? null, g.room)}>{t(language, "gen.openShort")}</button>
            )}
            <button type="button" className="gen-queue__dismiss" aria-label={t(language, "gen.dismiss")} onClick={() => dismiss(g.id)}>×</button>
          </li>
        ))}
      </ul>
      {onCreateAnother && (
        <button type="button" className="gen-queue__another" onClick={onCreateAnother}>{createLabel || t(language, "gen.createAnother")}</button>
      )}
    </div>
  );
}


// R222 — **THE FOUR MODAL DOORS ASK THE SERVER WHETHER THEY ARE RUNNING.**
//
// R220 named these four and declared them out of scope; this is that audit. Each modal read a purely
// local `busy` for its label and its `disabled`, and **a local flag dies on navigation** — so a
// member who starts a reading, leaves the modal and comes back meets an idle button on a paid
// generation that is already running, and a second tap buys it twice.
//
// **IT LIVES HERE RATHER THAN IN App.tsx BECAUSE `useActiveGenerations` DOES** — one poller, one
// server truth, which is that hook's own recorded ruling — and because a hook written three times in
// an eleven-thousand-line file is three chances for one of them to ask the wrong door (R28/R29).
// The DECISION is `modalRunState`'s, a pure law a `.mjs` gate RUNS; this is only the wiring.
export function useModalBusy(door: "mini" | "prediction" | "deep" | "bubble", localBusy: boolean): boolean {
  const { active } = useActiveGenerations(true);
  return modalRunState({ busy: { [door]: localBusy }, active }).running[door];
}

// R223 — the PHOTO doors, on the SAME poller for the same recorded reason. A second hook rather
// than a widened one because the two rosters are two questions (R18): a modal door is a reading
// composed from a chart she has already given us, and a photo door is one keyed on bytes she
// uploads afresh every time — which is the whole of why the dedupe folds the first and cannot fold
// the second. The DECISION is `photoRunState`'s, a pure law a `.mjs` gate RUNS; this is the wiring.
export function usePhotoBusy(door: "palm" | "tarot", localBusy: boolean): boolean {
  const { active } = useActiveGenerations(true);
  return photoRunState({ busy: { [door]: localBusy }, active }).running[door];
}
